/**
 * night_sky_model — the NIGHT SKY as the eye sees it, in numbers (the night-sky realism wave, 2026-10-06). PURE (no
 * three, no DOM): the star magnitudes, the natural sky's luminance and colour, the limiting magnitude a sky allows,
 * the contrast a star is drawn at, the Milky Way's visibility, extinction — every rule the sky shader draws
 * (lib/sky/night_sky.ts, star_field.ts mirror these constants in TSL) and the CPU sky sampler can read.
 *
 * UNITS. One "unit" is the natural night sky's zenith luminance, `sqmToNits(NATURAL_SQM)` ≈ 1.71·10⁻⁴ cd/m² — the
 * light-pollution contract's unit (lib/light_pollution.ts: an artificial sky of ratio R is R units). Into the scene a
 * unit is `unitNits × luxToScene` (THE PHYSICAL-EMISSIVE CONTRACT — `emissiveNits`, written every frame).
 *
 * THE STARS. The catalogue (the astrophoto's 114 k resolved stars, flux-sorted) carries a photo flux, not a magnitude,
 * and the photo SATURATED its bright end: the brightest star's flux is only 4× the 100th's where the real sky's is ~40×
 * (a log fit leaves 1.7 mag of error at the top). So a star's magnitude is its RANK matched to the real sky's star
 * counts (`STAR_COUNTS`, Tycho-2 + UCAC4 cumulative, whole sky): the k-th brightest catalogue star gets the magnitude
 * the k-th brightest real star has — Sirius −1.46 at rank 1, the 1,000th at 4.5, the 114,385th at 8.8. The counts are
 * then right by construction (~3× per magnitude).
 *
 * VISIBILITY = CONTRAST AGAINST THE LOCAL SKY. A star is drawn at a CONTRAST over the sky behind it that follows Pogson
 * from a threshold at the limiting magnitude: c(m) = STAR_C_THR · 10^(0.4·(NELM − m)), faded out over the last
 * magnitude (`starFade`). NELM is the Schaefer / Crumey limiting magnitude of the LOCAL sky (`nelmFromUnits` — the
 * contract's `nelmFromSqm` on the local luminance): a bright (light-polluted, moonlit, twilit) sky raises the
 * threshold and the faint stars go first; in the bright-sky regime NELM ≈ 8.35 − 2.5·log10(units) so a star's drawn
 * radiance stops depending on the sky at all — the sky simply rises over it (Weber). The real counts follow: ~4,500
 * stars over a pristine hemisphere, ~1,000 at NELM 5.5, ~150 at 4.
 *
 * THE NATURAL SKY (Jensen et al. 2001: airglow + zodiacal light + integrated starlight — "the sky is never completely
 * black"): 1 unit at the zenith, brightening toward the horizon like a van Rhijn emission layer seen through the air's
 * extinction (×2.6 at the horizon, ×1.9 at 10°, ×1.2 at 30° — `naturalShape`), a cool grey-blue overhead and a faint
 * green-grey airglow band low down (557.7 nm oxygen).
 *
 * THE NIGHT-FLOOR LIFT. The game lights a moonless ground with NIGHT_FLOOR_LUX (0.05 lux), 25× real starlight
 * (STARLIGHT_LUX 0.002) — its night compensation. Drawn at its physical 1.7·10⁻⁴ cd/m² the natural sky would sit four
 * stops UNDER that lifted ground (black), where the real sky is ~1.5× BRIGHTER than a moonless landscape (a dark
 * landscape against a lighter sky is the look of every dark-site photo). The natural sky, its Milky Way and its stars
 * — the sources of the starlight the floor stands in for — take the same factor: `naturalSkyLift(moon) =
 * nightFloorLux(moon) / STARLIGHT_LUX` (25 under a new moon, falling to 1 as a bright moon takes the floor away and its
 * own physical sky takes over).
 *
 * THE ADAPTED ARTIFICIAL SKY. The artificial sky (the contract's R units) rides the same lift, ADAPTED: displayed as
 * `ART_K·ln(1 + R/ART_K)` units (`artificialDisplayUnits`) — linear for the faint (a far city's dome keeps the
 * contract's contrast over the natural horizon: 5 km from a city the horizon toward it reads ~2× the horizon away),
 * logarithmic for the bright (the eye in a lamp-lit town is light-adapted: a town centre's sky reads ~4× a pristine one,
 * a city centre's ~6.5×, the capital's ~9×, not 9×, 31× and 121× — a dull grey-amber sky, never a dusk-bright one).
 * Measured (2026-10-06): drawn at its physical value beside the lifted natural sky, a far city's dome was 9 % of the
 * horizon — invisible (the bed); adapted with ART_K 6 the hub — R ≈ 28 under two capitals' domes 11–12 km away — read
 * a flat brown 80–90/255 (the world); ART_K 2 keeps a far dome at ~1.6× the horizon away from it. The limiting
 * magnitude always reads the PHYSICAL luminance (the eye's threshold is physics; the display is adaptation).
 *
 * Sources: Tycho-2 + UCAC4 star counts (hnsky.org/star_count.htm); Jensen, Durand, Stark, Premože, Dorsey, Shirley,
 * "A Physically-Based Nightsky Model", SIGGRAPH 2001; van Rhijn's function (e.g. Noll et al. 2012, A&A 543 A92);
 * Schaefer, "Astronomy and the limits of vision" (1993); Crumey, MNRAS 442 (2014); Bortle, Sky & Telescope (2001);
 * extinction ~0.2–0.3 mag/airmass at sea level (V); Schneegans et al., "Smaller than Pixels" (EG 2025) — a star's
 * light distributed over the pixels it covers.
 */
import { NATURAL_SQM, sqmToNits, nelmFromSqm, domeRadiance, type LightDome, type Skyglow } from '../light_pollution.js';
import { nightFloorLux, STARLIGHT_LUX } from '../photometry.js';

// ── the stars' magnitudes ────────────────────────────────────────────────────────────────────────────────────────────
/** Whole-sky cumulative star counts N(< m) — Tycho-2 + UCAC4 (hnsky.org), with the two brightest stars as anchors. */
export const STAR_COUNTS: ReadonlyArray<readonly [number, number]> = [
  [-1.46, 1], [-0.74, 2], [0, 3], [1, 14], [2, 55], [3, 191], [4, 558], [5, 1744], [6, 5326], [7, 16503],
  [8, 48559], [9, 139375], [10, 382925],
];

/** The magnitude of the `rank`-th brightest star of the real sky (1-based; log-linear between the count table's rows). */
export function magnitudeForRank(rank: number): number {
  const L = Math.log10(Math.max(1, rank));
  const t = STAR_COUNTS;
  for (let i = 1; i < t.length; i++) {
    const [m0, n0] = t[i - 1], [m1, n1] = t[i];
    const l0 = Math.log10(n0), l1 = Math.log10(n1);
    if (L <= l1 || i === t.length - 1) return m0 + ((m1 - m0) * (L - l0)) / (l1 - l0);
  }
  return t[t.length - 1][0];
}

/** Whole-sky count of stars brighter than `m` (the inverse of `magnitudeForRank`). */
export function starsBrighterThan(m: number): number {
  const t = STAR_COUNTS;
  if (m <= t[0][0]) return 0;
  for (let i = 1; i < t.length; i++) {
    const [m0, n0] = t[i - 1], [m1, n1] = t[i];
    if (m <= m1 || i === t.length - 1) return Math.pow(10, Math.log10(n0) + ((Math.log10(n1) - Math.log10(n0)) * (m - m0)) / (m1 - m0));
  }
  return t[t.length - 1][1];
}

/** Per-star magnitudes by flux RANK (ties keep catalogue order) — the catalogue's photo flux is saturated at the top. */
export function catalogMagnitudes(flux: ArrayLike<number>): Float32Array {
  const n = flux.length;
  const order = new Uint32Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  let sorted = true;
  for (let i = 1; i < n; i++) if (flux[i] > flux[i - 1]) { sorted = false; break; }
  if (!sorted) order.sort((a, b) => (flux[b] - flux[a]) || (a - b));
  const out = new Float32Array(n);
  for (let r = 0; r < n; r++) out[order[r]] = magnitudeForRank(r + 1);
  return out;
}

// ── the magnitude rides the star record's last float, beside the twinkle phase ──────────────────────────────────────
/** Magnitude offset and steps per magnitude of the packing (−4 … +12 mag at 1/64 mag; the phase is the fraction). */
export const MAG_PACK_OFFSET = 4;
export const MAG_PACK_STEPS = 64;
/** record[7] = round((m + 4)·64) + phase·0.999 — the space sky reads records 0..6 only, so the ground owns this float. */
export function packMagPhase(m: number, phase: number): number {
  const q = Math.round((Math.min(12, Math.max(-MAG_PACK_OFFSET, m)) + MAG_PACK_OFFSET) * MAG_PACK_STEPS);
  return q + Math.min(0.999, Math.max(0, phase)) * 0.999;
}
export function unpackMagPhase(w: number): { m: number; phase: number } {
  const q = Math.floor(w);
  return { m: q / MAG_PACK_STEPS - MAG_PACK_OFFSET, phase: (w - q) / 0.999 };
}

// ── units ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** One sky unit in cd/m² (the natural zenith; the contract's 1.71·10⁻⁴ per unit of R). */
export const UNIT_NITS = sqmToNits(NATURAL_SQM);
/** SQM and NELM of a sky of `units` (natural = 1). */
export function sqmFromUnits(units: number): number { return NATURAL_SQM - 2.5 * Math.log10(Math.max(1e-6, units)); }
export function nelmFromUnits(units: number): number { return nelmFromSqm(sqmFromUnits(units)); }

// ── the natural sky ──────────────────────────────────────────────────────────────────────────────────────────────────
/** The natural sky's brightening toward the horizon (van Rhijn × extinction, fitted): 1 + NAT_HORIZON_GAIN·(1 − sinAlt)³. */
export const NAT_HORIZON_GAIN = 1.6;
export function naturalShape(sinAlt: number): number {
  const c = 1 - Math.min(1, Math.max(0, sinAlt));
  return 1 + NAT_HORIZON_GAIN * c * c * c;
}
/** Luminance-normalised tints: a cool grey-blue overhead, a faint green-grey airglow band low down. */
export const NAT_TINT_ZENITH: readonly [number, number, number] = [0.9, 1.0, 1.2];
export const NAT_TINT_HORIZON: readonly [number, number, number] = [0.96, 1.04, 0.8];
/** Weight of the horizon tint: (1 − sinAlt)⁴. */
export function naturalTint(sinAlt: number): [number, number, number] {
  const c = 1 - Math.min(1, Math.max(0, sinAlt));
  const w = c * c * c * c;
  return [0, 1, 2].map((i) => NAT_TINT_ZENITH[i] + (NAT_TINT_HORIZON[i] - NAT_TINT_ZENITH[i]) * w) as [number, number, number];
}
/** THE NIGHT-FLOOR LIFT (see the header): the natural sky's display factor under a moon whose global is `moonGlobalLux`. */
export function naturalSkyLift(moonGlobalLux: number): number {
  return nightFloorLux(Math.max(0, moonGlobalLux)) / STARLIGHT_LUX;
}

/**
 * THE NIGHT DISPLAY GAIN (the coordinator, 2026-10-06): the whole night sky — the natural sky, the adapted skyglow,
 * the Milky Way and (through the sky they are drawn against) the stars — is drawn this many times over its physical
 * ratio to the lifted ground. At the true ratio a pristine zenith sat at 6.7/255 in the world (agent S's summit shot):
 * a dark-adapted eye sees the band and the stars far better than a display shows at that ratio, and the reference
 * photographs read a deep grey (~20/255), not black. It scales EVERY display term together, so a far town's dome keeps
 * its contrast over the natural horizon (a natural-only gain hid it: 1.09× at 10 km); a city sky brightens with it
 * (the hub ≈ 60 → ≈ 85/255 — the city photographs' range). The limiting magnitude and the ground are untouched. The
 * shader's uniform (SkyModule `uNightSky.natGain`) follows this value.
 */
export const NATURAL_DISPLAY_GAIN = 2.5;
let naturalDisplayGain = NATURAL_DISPLAY_GAIN;
/** The live gain (the lever `__nightSkyNatGain` moves it with the shader's uniform). */
export function naturalDisplayGainNow(): number { return naturalDisplayGain; }
export function setNaturalDisplayGain(v: number | null): number { naturalDisplayGain = v == null ? NATURAL_DISPLAY_GAIN : Math.max(0, v); return naturalDisplayGain; }

// ── the artificial sky (the contract's arithmetic, re-exported for one import) ──────────────────────────────────────
/** THE ADAPTED ARTIFICIAL SKY (header): the display units an artificial sky of `art` physical units is drawn at, before the lift. */
export const ART_K = 2;
export function artificialDisplayUnits(art: number): number { return ART_K * Math.log(1 + Math.max(0, art) / ART_K); }

/** The contract's uniform-glow shape: ×(1 + 1.2·(1 − sinAlt)²) — lib/light_pollution.artificialSkyRatio's. */
export function glowShape(sinAlt: number): number {
  const s = Math.max(0, sinAlt);
  return 1 + 1.2 * (1 - s) * (1 - s);
}

/**
 * The night sky's luminance along a unit direction, in units, both ways: `physical` (what the eye's threshold sees:
 * natural + artificial) and `display` ((natural + the adapted artificial) × the lift — what the shader draws), with the
 * display colour. The CPU mirror of lib/sky/night_sky.ts (the fog / the image-based light can read the same numbers:
 * scene radiance = rgb × UNIT_NITS × luxToScene).
 */
export function nightSkyUnits(glow: Pick<Skyglow, 'ratio' | 'tint'>, domes: ReadonlyArray<LightDome>, lift: number, vx: number, vy: number, vz: number): { physical: number; display: number; rgb: [number, number, number] } {
  const s = Math.max(0, vy);
  const nat = naturalShape(s);
  const nt = naturalTint(s);
  const g = glow.ratio * glowShape(s);
  let ar = glow.tint[0] * g, ag = glow.tint[1] * g, ab = glow.tint[2] * g;
  let art = g;
  for (const d of domes) {
    const v = domeRadiance(d, vx, vy, vz);
    art += v; ar += d.tint[0] * v; ag += d.tint[1] * v; ab += d.tint[2] * v;
  }
  const k = art > 1e-4 ? (artificialDisplayUnits(art) * lift) / art : lift;
  const G = naturalDisplayGain; // THE NIGHT DISPLAY GAIN (every display term together)
  return {
    physical: nat + art, display: (nat + artificialDisplayUnits(art)) * lift * G,
    rgb: [(nt[0] * nat * lift + ar * k) * G, (nt[1] * nat * lift + ag * k) * G, (nt[2] * nat * lift + ab * k) * G],
  };
}

// ── extinction ────────────────────────────────────────────────────────────────────────────────────────────────────────
/** V-band extinction, mag per airmass (sea level, clear — 0.2–0.3). */
export const EXT_K = 0.25;
/** Airmass, the shader's clamp: min(1 / max(sinAlt, 0.035), 14). */
export function airmass(sinAlt: number): number { return Math.min(1 / Math.max(sinAlt, 0.035), 14); }
export function extinctionMag(sinAlt: number): number { return EXT_K * (airmass(sinAlt) - 1); }

// ── how a star is drawn ──────────────────────────────────────────────────────────────────────────────────────────────
/** A star AT the limiting magnitude is drawn this much over its sky (before the fade). */
export const STAR_C_THR = 1.0;
/** The fade: full STAR_FADE_IN below NELM, gone STAR_FADE_OUT above it (the faint ones disappear first). */
export const STAR_FADE_IN = 0.75;
export const STAR_FADE_OUT = 0.25;
const sstep = (e0: number, e1: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
export function starFade(m: number, nelm: number): number { return 1 - sstep(nelm - STAR_FADE_IN, nelm + STAR_FADE_OUT, m); }
/** The contrast over the sky a star of (extincted) magnitude m is drawn at. */
export function starContrast(m: number, nelm: number): number {
  return STAR_C_THR * Math.pow(10, 0.4 * (nelm - m)) * starFade(m, nelm);
}
/** The faintest magnitude the shader still visits (its loop stops at the first fainter star — cells are bright-first). */
export function starCutoff(nelm: number): number { return nelm + STAR_FADE_OUT; }
/**
 * The pixel's share of a star's core: a Gaussian of σ STAR_CORE_PX pixels, box-integrated over the pixel and divided
 * by the share a centred star puts in its own pixel — a centred star reads exactly its contrast, an off-centre one
 * spreads the same light over two to four pixels and never vanishes between them (the anti-shimmer law, kept).
 */
export const STAR_CORE_PX = 0.45;
/** Glare (the eye's and the lens's): a soft halo of radius STAR_GLARE_PX pixels on stars brighter than STAR_GLARE_MAG. */
export const STAR_GLARE_MAG = 1.5;
export const STAR_GLARE_PX = 2.4;
export const STAR_GLARE_AMP = 0.035;
/** A star's colour: faint stars are colourless (scotopic), the bright keep a subtle hue. */
export function starSaturation(m: number): number { return 0.12 + 0.68 * sstep(2.5, -0.5, m); }

/** erf, Abramowitz–Stegun 7.1.26 (|ε| < 1.5·10⁻⁷) — the CPU twin of the shader's approximation. */
export function erf(x: number): number {
  const s = x < 0 ? -1 : 1, a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a);
  return s * y;
}
/** The box-integrated core at a pixel offset (px, py) from the star, normalised to 1 for a centred star. */
export function coreShare(px: number, py: number, sigmaPx = STAR_CORE_PX): number {
  const k = 1 / (Math.SQRT2 * sigmaPx);
  const c = erf(0.5 * k);
  const ix = (erf((px + 0.5) * k) - erf((px - 0.5) * k)) / (2 * c);
  const iy = (erf((py + 0.5) * k) - erf((py - 0.5) * k)) / (2 * c);
  return ix * iy;
}

// ── the Milky Way (the starless photo) ───────────────────────────────────────────────────────────────────────────────
/**
 * The photo's own dark-sky level (its p5 luminance, linear) and the excess that reads as one natural unit (its band,
 * p90 − p5): the photo's MILKY WAY = (lum − MW_FLOOR) / MW_UNIT units, gently compressed past one unit
 * (`mwUnitsFromExcess`: a processed astrophoto's core is ~6× the band where the real Sagittarius clouds are ~3–4× the
 * dark sky), capped at MW_MAX units.
 */
export const MW_FLOOR = 2.7e-3;
export const MW_UNIT = 0.02;
export const MW_COMPRESS = 0.75;
export const MW_MAX = 5;
export function mwUnitsFromExcess(excess: number): number {
  const x = Math.max(0, excess) / MW_UNIT;
  return Math.min(MW_MAX, x + (Math.pow(Math.max(x, 1e-6), MW_COMPRESS) - x) * sstep(0.9, 1.1, x)); // the shader's blend
}
/** Its chroma kept (the photo's warm core; to the eye the band is silvery). */
export const MW_SATURATION = 0.45;
/**
 * The band's visibility: an extended target needs a contrast over its sky (Blackwell / Crumey: ~0.1–0.3 for a large
 * target near 10⁻³ cd/m²) — fully seen at MW_C_FULL, gone at MW_C_GONE. The real sequence follows: obvious in a Bortle
 * 1–3 sky, weak in a 4–5, gone by a 6.
 */
export const MW_C_GONE = 0.08;
export const MW_C_FULL = 0.45;
export function mwVisibility(mwUnits: number, skyUnitsPhysical: number): number {
  return sstep(MW_C_GONE, MW_C_FULL, mwUnits / Math.max(1e-6, skyUnitsPhysical));
}
